
/**
 * NavigationHeaderBar.tsx
 *
 * Top-level navigation bar for the Ellucian Marketplace UI.
 * Handles responsive layout, navigation, authentication (sign in/out),
 * dropdown menus, and snackbar notifications. Integrates with Ellucian Design System.
 *
 * Usage: Place at the top of the app layout. Handles both desktop and mobile navigation.
 *
 * (c) 2025 Ellucian. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@ellucian/react-design-system/core';
import MobileHamburgerMenu from './MobileHamburgerMenu';
import { handleLogout as sharedHandleLogout } from '../../pages/RouterPage';
import { trackUserLogin } from '../../utils/analytics';

/**
 * Props for NavigationHeaderBar component.
 * @property snackbarOpen - Whether the snackbar is visible
 * @property onSnackbarClose - Handler to close the snackbar
 * @property snackbarMessage - Message to display in the snackbar
 * @property isAtTop - Whether the header is at the top of the page (affects styling)
 */
interface NavigationHeaderBarProps {
  snackbarOpen?: boolean;
  onSnackbarClose?: () => void;
  snackbarMessage?: React.ReactNode;
  isAtTop?: boolean;
}


/**
 * NavigationHeaderBar
 *
 * Renders the main navigation bar, including logo, navigation links, dropdown menus,
 * authentication (sign in/out), and responsive mobile menu. Also manages snackbar notifications.
 */
const NavigationHeaderBar: React.FC<NavigationHeaderBarProps> = ({ snackbarOpen, onSnackbarClose, snackbarMessage, isAtTop = true }) => {

  // State for dropdowns, authentication, and responsive layout
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  // ...existing code...
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const navigate = useNavigate();

  // Sync login state and email from localStorage, and listen for changes
  useEffect(() => {
  const isLoggedIn = localStorage.getItem('loggedIn') === 'true';
    setLoggedIn(isLoggedIn);
    const emailVal = localStorage.getItem('email') || '';
  // ...existing code...
    const onStorage = () => {
      setLoggedIn(localStorage.getItem('loggedIn') === 'true');
  // ...existing code...
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /**
   * Navigate to sign-in page
   */
  const handleSignInClick = () => {
    // Track login attempt
    trackUserLogin('okta_sso', false, 'marketplace_user');
    
    // Save current location before navigating to sign-in page
    if (window.location.pathname !== '/details/sign-in') {
      const redirectPath = window.location.pathname + window.location.search;
      localStorage.setItem('postLoginRedirect', redirectPath);
    }
    navigate('/details/sign-in', { state: { from: 'header' } });
  };

  /**
   * Log out user, clear localStorage, and reset state
   */
  const handleLogout = () => {
    setLoggedIn(false);
    setAvatarMenuOpen(false);
    window.dispatchEvent(new Event('loginStateChanged'));
    sharedHandleLogout();
  };

  // Close dropdown menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Add click outside functionality for avatar menu
  const avatarMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleAvatarClickOutside(event: MouseEvent) {
      if (
        avatarMenuRef.current &&
        !avatarMenuRef.current.contains(event.target as Node)
      ) {
        setAvatarMenuOpen(false);
      }
    }
    if (avatarMenuOpen) {
      document.addEventListener('mousedown', handleAvatarClickOutside);
    } else {
      document.removeEventListener('mousedown', handleAvatarClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleAvatarClickOutside);
    };
  }, [avatarMenuOpen]);

  // Refs for dropdown and menu positioning
  const wrapperRef = useRef<HTMLDivElement>(null);   // the relative container around button + menu
  const menuRef = useRef<HTMLDivElement>(null);      // the dropdown panel itself
  const chevronRef = useRef<HTMLDivElement>(null);

  // Snackbar positioning
  const learnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const snackbarRef = useRef<HTMLDivElement>(null);
  const [snackbarFixedLeft, setSnackbarFixedLeft] = useState<number | null>(null);
  const [snackbarFixedTop, setSnackbarFixedTop] = useState<number | null>(null);
  // Position snackbar below the navigation bar, aligned with Learn More
  useEffect(() => {
    if (snackbarOpen && learnMoreButtonRef.current && headerRef.current) {
      const btnRect = learnMoreButtonRef.current.getBoundingClientRect();
      const headerRect = headerRef.current.getBoundingClientRect();
      setSnackbarFixedLeft(btnRect.left + btnRect.width / 2 - 30); // Move 20px to the left for spacing
      setSnackbarFixedTop(headerRect.top + 58); // On the navigation bar, 12px from top
    } else {
      setSnackbarFixedLeft(null);
      setSnackbarFixedTop(null);
    }
  }, [snackbarOpen]);

  // Auto-hide snackbar after 4 seconds
  useEffect(() => {
    if (snackbarOpen && onSnackbarClose) {
      const timer = setTimeout(() => {
        onSnackbarClose();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [snackbarOpen, onSnackbarClose]);

  // Position dropdown menu so notch aligns with chevron
  useEffect(() => {
    if (!dropdownOpen) return;
    if (chevronRef.current && menuRef.current && wrapperRef.current) {
      const chevRect = chevronRef.current.getBoundingClientRect();
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const menuWidth = menuRef.current.offsetWidth;
      // chevron center X relative to the wrapper (which is position:relative)
      const chevronCenterX = (chevRect.left + chevRect.width / 2) - wrapperRect.left;
      // We want the notch's CENTER to be 16px from the right edge of the panel.
      // Notch width = 28, but since we set the image's left to (100% - 16px) with translateX(-50%),
      // we just keep 16 here for layout math.
      const desiredNotchOffsetFromRight = 16 + 28 / 2;
      // Position the panel so that the notch center ends up under the chevron center
      const panelLeft = Math.round(chevronCenterX - (menuWidth - desiredNotchOffsetFromRight) - 13);
      menuRef.current.style.left = `${panelLeft}px`;
    }
  }, [dropdownOpen]);

  // Listen for window resize to toggle mobile/desktop layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Snackbar positioned on the navigation bar, aligned with Learn More */}
      {snackbarOpen && snackbarFixedLeft && snackbarFixedTop && (
        <div
          ref={snackbarRef}
          style={{
            position: 'fixed',
            top: snackbarFixedTop,
            left: snackbarFixedLeft,
            transform: 'translateX(-50%)',
            zIndex: 2500,
            display: 'flex',
            width: 334,
            height: 40,
            alignItems: 'center',
            flexShrink: 0,
            borderRadius: '6px',
            background: 'var(--Neutral-neutral-500, #5B5E65)',
            boxShadow: '0 4px 4px 0 rgba(52, 63, 85, 0.06), 0 2px 12px 2px rgba(52, 63, 85, 0.06)',
            color: 'var(--Neutral-neutral-100, #FFF)',
            fontFamily: 'Noto Sans',
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            cursor: 'pointer',
            padding: '8px 16px',
            justifyContent: 'flex-start',
            gap: '8px',
          }}
          onClick={onSnackbarClose}
        >
          <Icon
            name="check-feedback"
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              color: '#FFF'
            }}
          />
          {snackbarMessage || 'Installation completed successfully'}
        </div>
      )}
      <header ref={headerRef} aria-label="Marketplace navigation header" title="Marketplace navigation header" style={{ display: 'flex', padding: '24px 0', justifyContent: 'space-between', width: '100%', boxSizing: 'border-box', overflow: 'visible', alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '100%', padding: '0 24px', boxSizing: 'border-box' }}>
          {/* Hamburger for mobile */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Hamburger menu button (now leftmost) */}
              <button
                className="mobile-hamburger"
                aria-label="Open menu"
                style={{
                  display: 'flex',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginRight: 16,
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => setMobileMenuOpen(true)}
              >
                <Icon name="menu" style={{ width: 16, height: 16, aspectRatio: '1/1', color: '#fff' }} />
              </button>
              {/* Logo (mobile) */}
              <div style={{ width: 82, height: 19.951, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <a href="https://www.ellucian.com" target="_blank" rel="noopener noreferrer" aria-label="Ellucian homepage (opens in a new window)" title="Ellucian homepage (opens in a new window)" style={{ display: 'flex', alignItems: 'center' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="82" height="19.951" viewBox="0 0 132 32" fill="none">
                    <g clipPath="url(#clip0_34_20333)">
                      <path d="M20.8116 0.742574H25.6956V31.5212H20.8116V0.742574ZM28.3353 31.5212H33.2193V0.742574H28.3353V31.5212ZM51.2148 24.0904C50.1148 25.9371 48.0028 27.0804 45.8469 27.0804C42.7668 27.0804 40.7429 24.9699 40.7429 21.4962V10.0201H35.8589V22.2876C35.8589 28.2235 39.2469 31.9609 44.6589 31.9609C47.3429 31.9609 49.9389 30.6858 51.2148 28.6192V31.5212H56.1428V10.0201H51.2148V24.0904ZM68.7705 27.2562C65.2065 27.2562 62.4785 24.4421 62.4785 20.7925C62.4785 17.0991 65.2065 14.285 68.7705 14.285C71.3224 14.285 73.5224 15.736 74.0505 17.7147H79.0665C78.4065 13.0539 73.9625 9.58031 68.7705 9.58031C62.4785 9.58031 57.6385 14.417 57.6385 20.7925C57.6385 27.1681 62.4785 31.9608 68.7705 31.9608C74.0065 31.9608 78.5384 28.5312 79.1104 23.6505H74.0944C73.5665 25.805 71.3224 27.2562 68.7705 27.2562ZM80.6507 31.5212H85.5347V10.0201H80.6507V31.5212ZM83.1147 0.0390625C81.3988 0.0390625 79.9908 1.49005 79.9908 3.16089C79.9908 4.91967 81.3988 6.3267 83.1147 6.3267C84.8305 6.3267 86.2827 4.91967 86.2827 3.16089C86.2827 1.49005 84.8307 0.0390625 83.1147 0.0390625ZM104.278 10.0201H109.206V31.5212H104.278V28.5313C103.222 30.5099 100.318 31.9609 97.4143 31.9609C91.5623 31.9609 87.0743 27.0363 87.0743 20.7487C87.0743 14.505 91.5624 9.58044 97.4143 9.58044C100.318 9.58044 103.222 11.0314 104.278 13.0101V10.0201ZM104.278 17.5829C103.266 15.6482 100.89 14.2412 98.4263 14.2412C94.7303 14.2412 91.8703 17.0113 91.8703 20.7487C91.8703 24.4861 94.7304 27.3001 98.4263 27.3001C100.89 27.3001 103.222 25.8931 104.278 24.0023V17.5828V17.5829ZM123.2 9.58044C120.516 9.58044 118.008 10.8556 116.688 12.8781V10.0201H111.76V31.5212H116.688V17.407C117.832 15.6043 119.856 14.461 122.012 14.461C125.092 14.461 127.116 16.5716 127.116 20.0452V31.5212H132V19.2537C132 13.3178 128.612 9.58044 123.2 9.58044ZM0 5.13952H18.172V0.742574H0V5.13952ZM4.4  13.9334H13.772V18.3304H4.4V22.7273H18.172V9.53647H4.4V13.9334ZM0 31.5212H18.172V27.1243H0V31.5212Z" fill="white" />
                    </g>
                    <defs>
                      <clipPath id="clip0_34_20333">
                        <rect width="132" height="32" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </a>
              </div>
              {/* Gap of 4px */}
              <div style={{ width: '4px' }}></div>
              {/* Vertical Line (mobile) */}
              <div style={{ width: '1px', height: '20px', opacity: 0.5, background: '#FFF', margin: '0 4px' }}></div>
              {/* Gap of 4px */}
              <div style={{ width: '4px' }}></div>
              {/* Word "Marketplace" (mobile) */}
              <div
                style={{
                  color: 'var(--Neutral-neutral-100, #FFF)',
                  fontFamily: 'Poppins, Futura, "Century Gothic"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '110%',
                  cursor: 'pointer',
                  userSelect: 'none',
                  flex: 1,
                  marginTop: '6px'
                }}
                onClick={() => navigate('/')}
                title="Go to Home"
                role="button"
                tabIndex={0}
                onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/'); }}
              >
                Marketplace
              </div>
              <MobileHamburgerMenu
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                loggedIn={loggedIn}
                onLogout={handleLogout}
              />
              {/* SIGN IN button or Avatar for mobile header */}
              {!loggedIn ? (
                <button
                  style={{
                    display: 'flex',
                    height: 32,
                    padding: '0 16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 8,
                    borderRadius: 4,
                    border: '1px solid #FFFFFF',
                    background: isAtTop ? 'transparent' : '#7100EB',
                    color: '#FFFFFF',
                    fontFamily: 'Poppins',
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: '24px',
                    letterSpacing: '0.42px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    userSelect: 'none',
                    boxSizing: 'border-box',
                    transition: 'background 0.3s ease',
                    marginLeft: 8
                  }}
                  onClick={handleSignInClick}
                >
                  SIGN IN
                </button>
              ) : (
                <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 32,
                      width: 32,
                      borderRadius: '50%',
                      border: '2px solid #fff',
                      background: '#fff',
                      cursor: 'pointer',
                      padding: 0,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                    onClick={() => setAvatarMenuOpen((open) => !open)}
                    aria-haspopup="true"
                    aria-expanded={avatarMenuOpen}
                  >
                      <Avatar id="header-avatar" color="purple" style={{ width: 32, height: 32, fontSize: 16 }}>
                        {(() => {
                          // Get userName from localStorage for avatar initials
                          const userName = localStorage.getItem('userName') || '';
                          if (userName) {
                            const words = userName.trim().split(/\s+/);
                            if (words.length === 1) {
                              return words[0][0].toUpperCase();
                            } else {
                              return (words[0][0] + words[1][0]).toUpperCase();
                            }
                          }
                          return 'A';
                        })()}
                      </Avatar>
                  </button>
                  {avatarMenuOpen && (
                    <div
                      ref={avatarMenuRef}
                      style={{
                        position: 'absolute',
                        top: 56,
                        right: 24,
                        background: '#fff',
                        border: '1px solid #B2B3B7',
                        borderRadius: 4,
                        boxShadow: '0 4px 16px rgba(52, 63, 85, 0.12)',
                        minWidth: 120,
                        zIndex: 2000,
                        padding: '8px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                      }}
                    >
                      <img
                        src={require('../../icons/Notch.svg').default}
                        alt="dropdown notch"
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: '16px',
                          transform: 'translateX(50%)',
                          width: 28,
                          height: 6,
                          zIndex: 2001,
                        }}
                      />
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#151618',
                          fontFamily: 'Inter, SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif',
                          fontSize: 14,
                          fontWeight: 400,
                          padding: '8px 16px',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Desktop header content (logo, nav, avatar, etc.) only for non-mobile */}
          {!isMobile && (
            <>
              {/* Ellucian Logo */}
              <div
                style={{
                  width: 132,
                  height: 32,
                  aspectRatio: '33/8',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <a href="https://www.ellucian.com" target="_blank" rel="noopener noreferrer" aria-label="Ellucian homepage (opens in a new window)" title="Ellucian homepage (opens in a new window)" style={{ display: 'flex', alignItems: 'center' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="132" height="32" viewBox="0 0 132 32" fill="none">
                    <g clipPath="url(#clip0_34_20333)">
                      <path d="M20.8116 0.742574H25.6956V31.5212H20.8116V0.742574ZM28.3353 31.5212H33.2193V0.742574H28.3353V31.5212ZM51.2148 24.0904C50.1148 25.9371 48.0028 27.0804 45.8469 27.0804C42.7668 27.0804 40.7429 24.9699 40.7429 21.4962V10.0201H35.8589V22.2876C35.8589 28.2235 39.2469 31.9609 44.6589 31.9609C47.3429 31.9609 49.9389 30.6858 51.2148 28.6192V31.5212H56.1428V10.0201H51.2148V24.0904ZM68.7705 27.2562C65.2065 27.2562 62.4785 24.4421 62.4785 20.7925C62.4785 17.0991 65.2065 14.285 68.7705 14.285C71.3224 14.285 73.5224 15.736 74.0505 17.7147H79.0665C78.4065 13.0539 73.9625 9.58031 68.7705 9.58031C62.4785 9.58031 57.6385 14.417 57.6385 20.7925C57.6385 27.1681 62.4785 31.9608 68.7705 31.9608C74.0065 31.9608 78.5384 28.5312 79.1104 23.6505H74.0944C73.5665 25.805 71.3224 27.2562 68.7705 27.2562ZM80.6507 31.5212H85.5347V10.0201H80.6507V31.5212ZM83.1147 0.0390625C81.3988 0.0390625 79.9908 1.49005 79.9908 3.16089C79.9908 4.91967 81.3988 6.3267 83.1147 6.3267C84.8305 6.3267 86.2827 4.91967 86.2827 3.16089C86.2827 1.49005 84.8307 0.0390625 83.1147 0.0390625ZM104.278 10.0201H109.206V31.5212H104.278V28.5313C103.222 30.5099 100.318 31.9609 97.4143 31.9609C91.5623 31.9609 87.0743 27.0363 87.0743 20.7487C87.0743 14.505 91.5624 9.58044 97.4143 9.58044C100.318 9.58044 103.222 11.0314 104.278 13.0101V10.0201ZM104.278 17.5829C103.266 15.6482 100.89 14.2412 98.4263 14.2412C94.7303 14.2412 91.8703 17.0113 91.8703 20.7487C91.8703 24.4861 94.7304 27.3001 98.4263 27.3001C100.89 27.3001 103.222 25.8931 104.278 24.0023V17.5828V17.5829ZM123.2 9.58044C120.516 9.58044 118.008 10.8556 116.688 12.8781V10.0201H111.76V31.5212H116.688V17.407C117.832 15.6043 119.856 14.461 122.012 14.461C125.092 14.461 127.116 16.5716 127.116 20.0452V31.5212H132V19.2537C132 13.3178 128.612 9.58044 123.2 9.58044ZM0 5.13952H18.172V0.742574H0V5.13952ZM4.4  13.9334H13.772V18.3304H4.4V22.7273H18.172V9.53647H4.4V13.9334ZM0 31.5212H18.172V27.1243H0V31.5212Z" fill="white" />
                    </g>
                    <defs>
                      <clipPath id="clip0_34_20333">
                        <rect width="132" height="32" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </a>
              </div>
              {/* Gap of 24px */}
              <div style={{ width: '24px' }}></div>
              {/* Vertical Line */}
              <div style={{ width: '1px', height: '32px', opacity: 0.5, background: '#FFF', marginTop: '8px' }}></div>
              {/* Gap of 24px */}
              <div style={{ width: '24px' }}></div>
              {/* Word "Marketplace" */}
              <div
                style={{
                  flex: '1 0 0',
                  color: 'var(--Neutral-neutral-100, #FFF)',
                  fontFamily: 'Poppins, Futura, "Century Gothic"',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '27px',
                  letterSpacing: '0.54px',
                  marginTop: '10px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => navigate('/')}
                title="Go to Home"
                role="button"
                tabIndex={0}
                onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/'); }}
              >
                Marketplace
              </div>
              {/* Nav and Dropdown */}
              <nav
                style={{
                  display: 'flex',
                  gap: '32px',
                  alignItems: 'center',
                  height: '32px',
                  flexWrap: 'nowrap',
                  fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                  fontWeight: 400
                }}
              >
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    color: 'var(--Neutral-neutral-100, #FFF)',
                    textAlign: 'right',
                    fontFamily: 'Poppins, Futura, "Century Gothic"',
                    fontSize: 18,
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '27px',
                    letterSpacing: '0.54px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                  onClick={() => window.open('https://www.ellucian.com/partners', '_blank')}
                >
                  Partners
                  <Icon
                    name="external-link"
                    style={{ width: '16px', height: '14.22px', marginTop: '0.89px', color: '#FFFFFF' }}
                  />
                </button>
                {/* CHANGED: wrapperRef is the positioning context for the menu */}
                <div
                  ref={wrapperRef}
                  style={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}
                >
                  <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                    <button
                      ref={learnMoreButtonRef}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        height: '27px',
                        color: 'var(--Neutral-neutral-100, #FFF)',
                        textAlign: 'right',
                        fontFamily: 'Poppins, Futura, "Century Gothic"',
                        fontSize: 18,
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '27px',
                        letterSpacing: '0.54px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: 'fit-content'
                      }}
                      onClick={() => setDropdownOpen((open) => !open)}
                      aria-haspopup="true"
                      aria-expanded={dropdownOpen}
                    >
                      Learn More
                      <div ref={chevronRef}>
                        <Icon name="chevron-down" style={{ width: '16px', height: '16px', color: '#FFFFFF' }} />
                      </div>
                    </button>
                  </div>
                  {/* Dropdown menu with notch attached */}
                  {dropdownOpen && (
                    <div
                      ref={menuRef}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: '50%',
                        transform: 'translateX(-55)',
                        width: 224,
                        // height: 212,
                        background: '#FFF',
                        borderRadius: 2,
                        border: '1px solid #B2B3B7',
                        opacity: 1,
                        boxShadow: '0 4px 16px rgba(52, 63, 85, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '1px',
                        zIndex: 1000,
                      }}>
                      <img
                        src={require('../../icons/Notch.svg').default}
                        alt="dropdown notch"
                        style={{
                          position: 'absolute',
                          top: -6,
                          left: 'calc(100% - 16px)',
                          transform: 'translateX(-50%)',
                          width: 28,
                          height: 6,
                          zIndex: 1001,
                        }}
                      />
                      <a href="https://www.ellucian.com/about-us" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', minHeight: '53px', minWidth: '240px', maxWidth: '340px', padding: '16px 20px', gap: '8px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box' }}><span style={{ fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto', overflowWrap: 'break-word', color: 'var(--Neutral-neutral-600, #151618)', letterSpacing: '0', fontSize: 14, fontStyle: 'normal', fontWeight: 400, lineHeight: '21px', whiteSpace: 'normal', wordBreak: 'break-word' }}>About Us</span></a>
                      <a href="https://www.ellucian.com/contact-us" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', minHeight: '53px', minWidth: '240px', maxWidth: '340px', padding: '16px 20px', gap: '8px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box' }}><span style={{ fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto', overflowWrap: 'break-word', color: 'var(--Neutral-neutral-600, #151618)', letterSpacing: '0', fontSize: 14, fontStyle: 'normal', fontWeight: 400, lineHeight: '21px', whiteSpace: 'normal', wordBreak: 'break-word' }}>Contact Us</span></a>
                      <a
                        href="/faq"
                        style={{ display: 'flex', alignItems: 'center', minHeight: '53px', minWidth: '240px', maxWidth: '340px', padding: '16px 20px', gap: '8px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box' }}
                        onClick={e => {
                          e.preventDefault();
                          setDropdownOpen(false);
                          navigate('/faq');
                        }}
                      >
                        <span style={{ fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto', overflowWrap: 'break-word', color: 'var(--Neutral-neutral-600, #151618)', letterSpacing: '0', fontSize: 14, fontStyle: 'normal', fontWeight: 400, lineHeight: '21px', whiteSpace: 'normal', wordBreak: 'break-word' }}>Frequently Asked Questions</span>
                      </a>
                      <a href="https://training.ellucian.com/learn/dashboard/channel/367" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', minHeight: '53px', minWidth: '240px', maxWidth: '340px', padding: '16px 20px', gap: '8px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box' }}><span style={{ fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto', overflowWrap: 'break-word', color: 'var(--Neutral-neutral-600, #151618)', letterSpacing: '0', fontSize: 14, fontStyle: 'normal', fontWeight: 400, lineHeight: '21px', whiteSpace: 'normal', wordBreak: 'break-word' }}>Training</span></a>
                      {/* <a href="#success" style={{ display: 'flex', alignItems: 'center', minHeight: '53px', minWidth: '240px', maxWidth: '340px', padding: '16px 20px', gap: '8px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box' }}><span style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif', overflowWrap: 'break-word', color: 'var(--Neutral-neutral-600, #151618)', letterSpacing: '0', fontSize: 14, fontStyle: 'normal', fontWeight: 400, lineHeight: '21px', whiteSpace: 'normal', wordBreak: 'break-word' }}>Success Stories</span></a> */}
                    </div>
                  )}
                </div>
                {/* SIGN IN or Avatar logic */}
                {!loggedIn ? (
                  <button
                    style={{
                      display: 'flex',
                      height: 32,
                      padding: '0 16px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 8,
                      borderRadius: 4,
                      border: '1px solid #FFFFFF',
                      background: isAtTop ? 'transparent' : '#7100EB',
                      color: '#FFFFFF',
                      fontFamily: 'Poppins, Futura, "Century Gothic"',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '24px',
                      letterSpacing: '0.42px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      userSelect: 'none',
                      boxSizing: 'border-box',
                      transition: 'background 0.3s ease',
                    }}
                    onClick={handleSignInClick}
                  >
                    SIGN IN
                  </button>
                ) : (
                  <div ref={avatarMenuRef} style={{ position: 'relative' }}>
                    <button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 32,
                        width: 32,
                        borderRadius: '50%',
                        border: '2px solid #fff',
                        background: '#fff',
                        cursor: 'pointer',
                        padding: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                      onClick={() => setAvatarMenuOpen((open) => !open)}
                      aria-haspopup="true"
                      aria-expanded={avatarMenuOpen}
                    >
                      <Avatar id="header-avatar" color="purple" style={{ width: 32, height: 32, fontSize: 16 }}>
                          {(() => {
                            // Get userName from localStorage for avatar initials
                            const userName = localStorage.getItem('userName') || '';
                            if (userName) {
                              const words = userName.trim().split(/\s+/);
                              if (words.length === 1) {
                                return words[0][0].toUpperCase();
                              } else {
                                return (words[0][0] + words[1][0]).toUpperCase();
                              }
                            }
                            return 'A';
                          })()}
                      </Avatar>
                    </button>
                    {avatarMenuOpen && (
                      <div
                        ref={avatarMenuRef}
                        style={{
                          position: 'absolute',
                          top: 40,
                          right: 0,
                          background: '#fff',
                          border: '1px solid #B2B3B7',
                          borderRadius: 4,
                          boxShadow: '0 4px 16px rgba(52, 63, 85, 0.12)',
                          minWidth: 120,
                          zIndex: 2000,
                          padding: '8px 0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                        }}
                      >
                        <img
                          src={require('../../icons/Notch.svg').default}
                          alt="dropdown notch"
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: '16px',
                            transform: 'translateX(50%)',
                            width: 28,
                            height: 6,
                            zIndex: 2001,
                          }}
                        />
                        <button
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#151618',
                            fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                            fontSize: 14,
                            fontWeight: 400,
                            padding: '8px 16px',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                          onClick={handleLogout}
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </nav>
            </>
          )}
        </div>
      </header>
    </>
  );
}

export { NavigationHeaderBar };